import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { AccordionMenu } from '../../molecules/accordion-menu/accordion-menu.component';
import {
  AccordionExpandPosition,
  AccordionItem,
} from '../../../models/accordion.models';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'ui-category-sidebar-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AccordionMenu,
    NzCheckboxModule,
    ReactiveFormsModule,
    TranslocoDirective,
  ],
  template: `
    <ui-accordion-menu
      [panels]="panels()"
      [expandIconPosition]="expandIconPosition()"
      (onSubitemSelect)="emitSubitemSelect($event)"
    />
    <ng-template #headerTemplate let-data>
      <ng-container *transloco="let t">
        <div class="flex flex-row py-2 px-1 justify-start items-center">
          @if (form) {
            <form [formGroup]="getPanelForm(data.index)">
              <label nz-checkbox formControlName="checked">
                {{ t(data.title) }}
              </label>
            </form>
          }

          @if (data?.count) {
            <span class="bg-error px-2.5 rounded-full text-white text-xxs">
              {{ data!.count }}
            </span>
          }
        </div>
      </ng-container>
    </ng-template>
  `,
})
export class CategorySidebarMenuComponent {
  menu = input.required<AccordionItem[]>();
  expandIconPosition = input<AccordionExpandPosition>('end');
  onSubitemSelect = output<{
    itemId: string | null;
    subitemId: string | null;
    itemActive?: boolean;
  }>();
  onFormChange = output<{ id: string; checked: boolean }[]>();

  panels = computed(() => {
    return this.menu().map((panel, index) => ({
      ...panel,
      header: this.headerTemplate()!,
      index,
      context: { ...panel.context, index, id: panel.id },
    }));
  });
  headerTemplate = viewChild<TemplateRef<any>>('headerTemplate');
  form: FormGroup;

  /**
   * Initializes the CategorySidebarMenuComponent and sets up the form group for panels.
   * @param fb - FormBuilder instance for creating form controls.
   */
  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      panels: this.fb.array([]),
    });
  }

  /**
   * Angular lifecycle hook. Initializes the form and subscribes to value changes to emit form changes.
   */
  ngOnInit() {
    this.initializeForm();
    this.form.valueChanges.subscribe((value) => {
      this.onFormChange.emit(value.panels);
    });
  }

  /**
   * Initializes the form array for the panels based on the menu input.
   */
  initializeForm() {
    const panelControls = this.menu().map((panel) =>
      this.fb.group({
        id: panel.id,
        checked: [panel.checked || false],
      }),
    );
    this.form.setControl('panels', this.fb.array(panelControls));
  }

  /**
   * Returns the FormArray containing all panel controls.
   */
  get panelsArray(): FormArray {
    return this.form.get('panels') as FormArray;
  }

  /**
   * Returns the FormGroup for a specific panel by index.
   * @param index - The index of the panel.
   * @returns The FormGroup for the panel.
   */
  getPanelForm(index: number): FormGroup {
    return this.panelsArray.at(index) as FormGroup;
  }

  /**
   * Emits the subitem selection event to the parent component.
   * @param event - Object containing itemId, subitemId, and optional itemActive.
   */
  emitSubitemSelect(event: {
    itemId: string | null;
    subitemId: string | null;
    itemActive?: boolean;
  }) {
    const { itemId, subitemId, itemActive } = event;
    this.onSubitemSelect.emit({
      itemId,
      subitemId,
      itemActive,
    });
  }

  /**
   * Checks the form control for a given panel by controlId and sets it to true if not already checked.
   * @param controlId - The ID of the panel control to check.
   */
  checkFormControl(controlId: string) {
    const panels = this.form.value.panels;
    const controlIndex = Object.values(panels).findIndex(
      (panel: any) => panel.id === controlId,
    );
    const checkedControl = this.panelsArray.at(controlIndex).get('checked');
    if (!checkedControl?.value) checkedControl?.setValue(true);
  }
}
